import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { AuthContextProp } from '../interfaces/Auth';
import { userService, clearAllCache, PinnerProData, apiService } from '../services';

// Estendi l'interfaccia per includere più funzionalità
interface ExtendedAuthContextProp extends AuthContextProp {
  userProfile: PinnerProData | null;
  isLoading: boolean;
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<PinnerProData>) => Promise<void>;
}

export const AuthContext = createContext<ExtendedAuthContextProp>({
  isAuthenticated: null,
  authToken: null,
  userId: null,
  userProfile: null,
  isLoading: true,
  login: () => { },
  logout: () => { },
  refreshUserProfile: async () => { },
  updateUserProfile: async () => { },
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<PinnerProData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Funzione per ottenere il token dal refresh token
  const getAuthTokenFromRefresh = useCallback(async (refreshToken: string) => {
    try {
      const response = await apiService.post('/users/token_refresh/', {
        refresh: refreshToken
      });
      return response.data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }, []);

  // Funzione per caricare il profilo utente
  const loadUserProfile = useCallback(async () => {
    try {
      const profile = await userService.getAuthProfile();
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  }, []);

  const initAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const storedAuthToken = sessionStorage.getItem('authToken');
      const storedUserId = sessionStorage.getItem('userId');

      if (refreshToken) {
        try {
          const data = await getAuthTokenFromRefresh(refreshToken);
          sessionStorage.setItem('authToken', data.access);
          sessionStorage.setItem('userId', data.user.id);
          setAuthToken(data.access);
          setUserId(data.user.id);
          setIsAuthenticated(true);
          await loadUserProfile();
          // Notifica il cambio di stato di autenticazione
          window.dispatchEvent(new CustomEvent('authStateChanged'));
        } catch {
          localStorage.removeItem('refreshToken');
          sessionStorage.clear();
          setIsAuthenticated(false);
        }
      } else if (storedAuthToken && storedUserId) {
        setAuthToken(storedAuthToken);
        setUserId(storedUserId);
        setIsAuthenticated(true);
        await loadUserProfile();
        // Notifica il cambio di stato di autenticazione
        window.dispatchEvent(new CustomEvent('authStateChanged'));
      } else {
        setIsAuthenticated(false);
      }
    } catch (e) {
      console.error('Error initializing auth:', e);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthTokenFromRefresh, loadUserProfile]);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    const onPageShow = (e: any) => {
      if (e?.persisted) {
        initAuth();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [initAuth]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (['refreshToken', 'authToken', 'userId'].includes(e.key ?? '')) {
        initAuth();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [initAuth]);

  // Funzione di login migliorata
  const login = useCallback(async (token: string, userId: string, refreshToken?: string) => {
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('userId', userId);

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    setAuthToken(token);
    setUserId(userId);
    setIsAuthenticated(true);

    // Carica il profilo utente dopo il login
    await loadUserProfile();

    // Notifica il cambio di stato di autenticazione per aggiornare i crediti
    window.dispatchEvent(new CustomEvent('authStateChanged'));

    // Forza anche il caricamento diretto dei crediti per sicurezza
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('forceLoadCredits'));
    }, 500);
  }, [loadUserProfile]);

  // Funzione di logout migliorata
  const logout = useCallback(() => {
    // Reindirizza l'utente alla pagina di landing page
    window.location.href = 'https://www.pinnalo.it';

    clearAllCache();

    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshToken');

    sessionStorage.clear();
    localStorage.clear();

    setAuthToken(null);
    setUserId(null);
    setUserProfile(null);
    setIsAuthenticated(false);

    // Notifica il cambio di stato di autenticazione per resettare i crediti
    window.dispatchEvent(new CustomEvent('authStateChanged'));
  }, []);

  // Funzione per aggiornare il profilo utente
  const refreshUserProfile = useCallback(async () => {
    const profile = await userService.getAuthProfile(true); // Force refresh
    setUserProfile(profile);
  }, []);

  // Funzione per aggiornare i dati del profilo
  const updateUserProfile = useCallback(async (data: Partial<PinnerProData>) => {
    const updatedProfile = await userService.updateProfile(data);
    setUserProfile(updatedProfile);
  }, []);

  // Memorizza il valore del context per evitare re-render non necessari
  const value = useMemo(
    () => ({
      isAuthenticated,
      authToken,
      userId,
      userProfile,
      isLoading,
      login,
      logout,
      refreshUserProfile,
      updateUserProfile
    }),
    [isAuthenticated, authToken, userId, userProfile, isLoading, login, logout, refreshUserProfile, updateUserProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};